import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { User } from 'types/users';
import type { FilterTypes } from 'common/common_components/Table/types';
import type { Note } from 'components/Admin/types';
import type { SubscriptionsDto } from 'components/Admin/organisms/Subscriptions/types';

export type GetUsersQueryParams = {
  [FilterTypes.isArchived]: number;
  [FilterTypes.activeSubscription]: number;
  [FilterTypes.activeRental]: number;
};
type UserNotesPendingStatus = {
  general?: boolean;
  specificNote?: string | null;
};

interface InitialState {
  items: Array<User>;
  selectedUser: User | null;
  secondaryDriver: Partial<User> | null;
  profile: User | null;
  newUser: User | null;
  isPending: boolean;
  notes: Array<Note>;
  passwordIsPending: boolean;
  isUserNotesPending: UserNotesPendingStatus;
  token: string | null;
  isSecondaryDriverCreating: boolean;
  isSocialLoginPending: boolean;
}

const initialState: InitialState = {
  items: [],
  profile: null,
  isPending: false,
  newUser: null,
  selectedUser: null,
  notes: [],
  passwordIsPending: false,
  isUserNotesPending: {
    general: false,
    specificNote: null,
  },
  secondaryDriver: null,
  token: localStorage.getItem('token') || null,
  isSecondaryDriverCreating: false,
  isSocialLoginPending: false,
};
export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setIsSocialLoginPending: (state, action) => {
      return {
        ...state,
        isSocialLoginPending: action.payload,
      };
    },
    setIsSecondaryDriverCreating: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isSecondaryDriverCreating: action.payload,
      };
    },
    resetStore: (state) => {
      return {
        ...state,
        ...initialState,
      };
    },
    setAccessToken: (state, action: PayloadAction<string | null>) => {
      if (action.payload) {
        localStorage.setItem('token', action.payload);
      }
      return {
        ...state,
        token: action.payload,
      };
    },
    resetSecondaryDriver: (state) => {
      return {
        ...state,
        secondaryDriver: null,
      };
    },
    changeSecondaryDriver: (state, action: PayloadAction<Partial<User>>) => {
      return {
        ...state,
        secondaryDriver: {
          ...state.secondaryDriver,
          ...action.payload,
        },
      };
    },
    resetDraftUser: (state) => {
      return {
        ...state,
        newUser: null,
      };
    },
    setNewDraftUser: (state, action) => {
      return {
        ...state,
        newUser: {
          ...state.newUser,
          ...action.payload,
        },
      };
    },
    setPasswordPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        passwordIsPending: action.payload,
      };
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      return {
        ...state,
        selectedUser: action.payload,
      };
    },
    removeUser: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };
    },
    updateUser: (state, action: PayloadAction<User>) => {
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
    setUserNotesPendingStatus: (
      state,
      action: PayloadAction<UserNotesPendingStatus>,
    ) => {
      return {
        ...state,
        isUserNotesPending: action.payload,
      };
    },
    setUserNotes: (state, action: PayloadAction<Array<Note>>) => {
      return {
        ...state,
        notes: action.payload,
      };
    },
    setNewNote: (state, action: PayloadAction<Note>) => {
      return {
        ...state,
        notes: state.notes.concat(action.payload),
      };
    },
    deleteNote: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        notes: state.notes.filter((item) => item.id !== action.payload),
      };
    },
    updateNote: (state, action: PayloadAction<Note>) => {
      return {
        ...state,
        notes: state.notes.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
    deleteUsers: (state, action: PayloadAction<Array<string>>) => {
      return {
        ...state,
        items: state.items.filter(
          (user) => !action.payload.includes(user.id as string),
        ),
      };
    },
    applyArchivedUsers: (state, action: PayloadAction<Array<string>>) => {
      return {
        ...state,
        items: state.items.map((item) =>
          action.payload.includes(item.id as string)
            ? { ...item, isArchived: true }
            : item,
        ),
      };
    },
    setUsersPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isPending: action.payload,
      };
    },
    setAllUsers: (state, action: PayloadAction<Array<User>>) => {
      return {
        ...state,
        items: action.payload,
      };
    },
    setNewUser: (state, action: PayloadAction<User>) => {
      return {
        ...state,
        items: state.items.concat(action.payload),
      };
    },
    setProfile: (state, action: PayloadAction<User | null>) => {
      return {
        ...state,
        profile: action.payload,
      };
    },

    updateProfileSubscription: (
      state,
      action: PayloadAction<SubscriptionsDto>,
    ) => {
      return {
        ...state,
        profile: {
          ...(state.profile as User),
          subscriptions: (state.profile?.subscriptions || []).map((item) =>
            item.id === action.payload.id ? action.payload : item,
          ),
        },
      };
    },
    setProfileSubscription: (
      state,
      action: PayloadAction<SubscriptionsDto>,
    ) => {
      return {
        ...state,
        profile: {
          ...(state.profile as User),
          subscriptions: (state.profile?.subscriptions || []).concat(
            action.payload,
          ),
        },
      };
    },
  },
});
